import { parsePhoneNumber } from 'awesome-phonenumber';
import { getCategoryText } from '@habity/services';
import type { CategoryType, NestedAddress, NestedStatus } from '@habity/types';

/** End of the intro sentence in the default speculator message; used to insert the structural-details sentence after it. */
export const SPECULATOR_MESSAGE_INTRO_END = 'som jag precis fått in.';

/** Question sentence used in the default speculator message; URL is inserted after it. */
export const SPECULATOR_MESSAGE_QUESTION =
  'Skulle det vara av intresse att komma på visning?';

/** Input for message helpers: real DB fields from address/status so show_address is properly typed. */
export type PropertyDetails = Pick<NestedAddress, 'street'> & {
  category?: NestedAddress['category'] | string;
  rooms?: number | null;
  living_space?: number | null;
  fullAddress?: string;
  show_address?: NestedStatus['show_address'];
};

/**
 * Build PropertyDetails from address + home relation (e.g. ListButtons fetch).
 * Use this so details passed to buildIntroLine are always correct.
 */
export function toPropertyDetails(params: {
  street?: string | null;
  category?: NestedAddress['category'] | string;
  rooms?: number | null;
  living_space?: number | null;
  show_address?: NestedStatus['show_address'];
}): PropertyDetails {
  return {
    street: params.street ?? undefined,
    category: params.category,
    rooms: params.rooms ?? undefined,
    living_space: params.living_space ?? undefined,
    show_address: params.show_address,
  } as PropertyDetails;
}

export interface UserDetails {
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  email?: string | null;
}

function buildIntroLine(details: PropertyDetails): string {
  const { category, street, rooms, show_address } = details;
  const categorySwedish =
    (category && (getCategoryText(category as CategoryType) || category)) ||
    'bostad';
  const categoryLower =
    categorySwedish.charAt(0).toLowerCase() + categorySwedish.slice(1);

  const descriptionPart = show_address
    ? `${categoryLower} på ${street}`
    : `${categoryLower}`;
  return `Hej,

Jag ser att du letar bostad i området och skulle bara vilja tipsa om en ${descriptionPart} ${SPECULATOR_MESSAGE_INTRO_END}`;
}

function buildBrokerFooter(userDetails?: UserDetails): string {
  const userName =
    userDetails?.firstName || userDetails?.lastName
      ? `${userDetails.firstName || ''} ${userDetails.lastName || ''}`.trim()
      : '';
  if (!userName) return '';

  const contactParts: string[] = [];
  if (userDetails?.email) contactParts.push(userDetails.email);
  if (userDetails?.phone) {
    const parsed = parsePhoneNumber(userDetails.phone, { regionCode: 'SE' });
    const formattedPhone = parsed.valid
      ? parsed.number?.national || userDetails.phone
      : userDetails.phone;
    contactParts.push(formattedPhone);
  }
  const contactInfo = contactParts.join(' | ');
  return `Med vänlig hälsning,
${userName}
Erik Olsson Fastighetsförmedling${contactInfo ? `\n${contactInfo}` : ''}`;
}

export function getDefaultMessageTitle(
  category: string,
  fullAddress: string,
  show_address?: NestedStatus['show_address']
): string {
  const categoryLower = category.charAt(0).toLowerCase() + category.slice(1);
  return show_address === true
    ? `Tips om ${categoryLower} på ${fullAddress}`
    : `Tips om ${fullAddress}`;
}

export function getDefaultMessageBody(
  details: PropertyDetails,
  userDetails?: UserDetails
): string {
  const intro = buildIntroLine(details);
  const footer = buildBrokerFooter(userDetails);
  return `${intro}

${SPECULATOR_MESSAGE_QUESTION}${footer ? `\n\n${footer}` : ''}`;
}

/**
 * Default matching-email message: same intro as plaintext (cut after "som jag precis fått in.") plus broker footer.
 */
export function getMatchingEmailDefaultMessage(
  details: PropertyDetails,
  userDetails?: UserDetails
): string {
  const intro = buildIntroLine(details);
  const footer = buildBrokerFooter(userDetails);
  return footer ? `${intro}\n\n${footer}` : intro;
}

function insertAfterSegment(
  text: string,
  marker: string,
  toInsert: string
): string {
  const idx = text.indexOf(marker);
  if (idx === -1) return text;
  const insertAt = idx + marker.length;
  const before = text.slice(0, insertAt);
  const after = text.slice(insertAt).trimStart();
  return after
    ? `${before}\n\n${toInsert}\n\n${after}`
    : `${before}\n\n${toInsert}`;
}

/**
 * Personal message: same intro (property details) + question as plaintext, with optional details sentence and URL inserted.
 * Use when the modal has homePreview so the personal template includes all property details and the home details/URL.
 */
export function getPersonalMessageBody(
  details: PropertyDetails,
  userDetails?: UserDetails,
  options?: { detailsSentence?: string | null; url?: string | null }
): string {
  const base = getDefaultMessageBody(details, userDetails);
  if (!options?.detailsSentence && !options?.url) return base;
  let result = base;
  if (options.detailsSentence) {
    result = insertAfterSegment(
      result,
      SPECULATOR_MESSAGE_INTRO_END,
      options.detailsSentence
    );
  }
  if (options.url) {
    result = insertAfterSegment(
      result,
      SPECULATOR_MESSAGE_QUESTION,
      options.url
    );
  }
  if (options.url && !result.includes(options.url)) {
    result = result + '\n\n' + options.url;
  }
  return result;
}
